import React, { useMemo } from 'react';

/**
 * EstimatSubItemRow - Renderar en underrad (subItem) för ett estimat-item
 * Endast synlig för byggkonsult, dold för kund
 *
 * @param {Object} subItem - SubItem-data med id, label, quantity, price, unit, selected
 * @param {string} categoryKey - Kategori-nyckel (t.ex. 'inkopFastInredning')
 * @param {string} itemName - Parent item-nyckel (t.ex. 'Garderober/förvaring')
 * @param {Function} updateSubItem - Funktion för att uppdatera subItem
 * @param {Function} removeSubItem - Funktion för att ta bort subItem
 */
const EstimatSubItemRow = ({
  subItem,
  categoryKey,
  itemName,
  updateSubItem,
  removeSubItem,
}) => {
  // Beräkna total för subItem
  const total = useMemo(() => {
    if (!subItem.selected || subItem.quantity <= 0) return 0;
    const price = typeof subItem.price === 'object'
      ? (subItem.price?.totalt || 0)
      : (subItem.price || 0);
    return price * subItem.quantity;
  }, [subItem.selected, subItem.price, subItem.quantity]);

  return (
    <tr className="bg-gray-50 border-l-4 border-blue-200">
      {/* Ändra-kolumn - tom för underrader men ta bort-knapp */}
      <td className="px-1 sm:px-2 py-1.5 sm:py-2 text-center align-middle whitespace-nowrap">
        <button
          onClick={() => removeSubItem(categoryKey, itemName, subItem.id)}
          className="text-red-500 hover:text-red-700 p-1 rounded focus:outline-none focus:ring-2 focus:ring-red-300"
          aria-label="Ta bort underrad"
          title="Ta bort underrad"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
          </svg>
        </button>
      </td>

      {/* Arbetsmoment - indenterad med ↳ symbol */}
      <td className="px-2 sm:px-4 py-1.5 sm:py-2 text-sm text-gray-700">
        <div className="flex items-center gap-2 pl-2 sm:pl-4">
          <span className="text-gray-400">↳</span>
          <input
            type="text"
            value={subItem.label || ''}
            onChange={(e) => updateSubItem(categoryKey, itemName, subItem.id, 'label', e.target.value)}
            className="flex-1 px-1.5 sm:px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink bg-white"
            placeholder="Underrad"
          />
        </div>
      </td>

      {/* Välj */}
      <td className="px-2 sm:px-4 py-1.5 sm:py-2 text-left whitespace-nowrap">
        <input
          type="checkbox"
          checked={subItem.selected || false}
          onChange={(e) => updateSubItem(categoryKey, itemName, subItem.id, 'selected', e.target.checked)}
          className="rounded border-gray-300 text-leadme-pink focus:ring-leadme-pink"
        />
      </td>

      {/* A-pris */}
      <td className="px-2 sm:px-4 py-1.5 sm:py-2 text-center text-sm text-gray-700 whitespace-nowrap">
        <input
          type="number"
          min="0"
          step="1"
          value={typeof subItem.price === 'object' ? (subItem.price?.totalt || 0) : (subItem.price || 0)}
          onChange={(e) => updateSubItem(categoryKey, itemName, subItem.id, 'price', parseFloat(e.target.value) || 0)}
          className="w-16 sm:w-24 px-2 py-1 text-right text-xs sm:text-sm border border-gray-200 rounded focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
          disabled={!subItem.selected}
        />
      </td>

      {/* Antal */}
      <td className="px-2 sm:px-4 py-1.5 sm:py-2 text-center whitespace-nowrap">
        <input
          type="number"
          min="0"
          value={subItem.quantity || 0}
          onChange={(e) => updateSubItem(categoryKey, itemName, subItem.id, 'quantity', parseFloat(e.target.value) || 0)}
          onFocus={(e) => e.target.select()}
          className="w-12 sm:w-16 px-2 py-1 text-center text-xs sm:text-sm border border-gray-200 rounded focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
          disabled={!subItem.selected}
        />
      </td>

      {/* Enhet */}
      <td className="px-2 sm:px-4 py-1.5 sm:py-2 text-center text-sm text-gray-600 whitespace-nowrap">
        <select
          value={subItem.unit || 'st'}
          onChange={(e) => updateSubItem(categoryKey, itemName, subItem.id, 'unit', e.target.value)}
          className="px-1 sm:px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink bg-white"
          aria-label="Ändra enhet"
        >
          <option value="h">h</option>
          <option value="st">st</option>
          <option value="lpm">lpm</option>
          <option value="kvm">kvm</option>
        </select>
      </td>

      {/* Summa */}
      <td className="px-2 sm:px-4 py-1.5 sm:py-2 text-left text-sm text-gray-600">
        {total > 0 ? `${total.toLocaleString('sv-SE')} kr` : '-'}
      </td>

      {/* Kommentarer - tom för underrader, dold på mobil */}
      <td className="p-0 sm:px-4 sm:py-2 text-center text-gray-400 text-xs w-0 max-w-0 sm:max-w-none sm:w-auto overflow-hidden">
        <span className="hidden sm:inline">-</span>
      </td>
    </tr>
  );
};

export default EstimatSubItemRow;
